import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import type {
  ProjectPickerBaseProject as ProjectPickerRelayProject,
  ProjectPickerBaseProps,
} from '@github-ui/item-picker-project'
import {ProjectPicker} from '@github-ui/item-picker-project-relay'
import {BulkIssueProjectPicker as BulkIssueProjectPickerTsq} from '@github-ui/item-picker-project-tsq/BulkIssueProjectPicker'
import type React from 'react'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {commitAddIssuesToProjectPickerBulkMutation} from './add-issues-to-project-picker-bulk-mutation'

/**
 * Props for ProjectPicker component from relay package.
 * Moved here as it's only used in this file.
 * Extends ProjectPickerBaseProps with relay-specific requirements.
 */
type ProjectPickerProps = Omit<ProjectPickerBaseProps, 'onSelectionChange'> & {
  /** Unique identifier for the picker instance */
  pickerId: string
  /** Repository name */
  repo: string
  /** Repository owner */
  owner: string
  /** Currently selected projects */
  selectedProjects: ProjectPickerRelayProject[]
  /** Callback when projects are saved */
  onSave: (projects: ProjectPickerRelayProject[]) => void
  /** Optional function to filter/transform selected projects */
  getSelectedProjects?: (projects: ProjectPickerRelayProject[]) => ProjectPickerRelayProject[]
}

export type BulkProjectPickerProps = Omit<ProjectPickerProps, 'onSave'> &
  Omit<SharedBulkActionsItemPickerProps, 'issuesToActOn'> & {
    issueIds: string[]
  }

export const BulkProjectPicker: React.FC<BulkProjectPickerProps> = props => {
  const isTsqPickerEnabled = isFeatureEnabled('item_picker_project_tsq_migration')

  if (isTsqPickerEnabled) {
    return <BulkIssueProjectPickerTsq {...props} onMutate={() => {}} />
  }

  return <BulkProjectPickerRelay {...props} />
}

const BulkProjectPickerRelay: React.FC<BulkProjectPickerProps> = ({
  pickerId,
  issueIds,
  readonly,
  selectedProjects,
  insidePortal,
  anchorElement,
  onCompleted,
  onError,
  nested = false,
  owner,
  repo,
  triggerOpen = false,
}) => {
  const environment = useRelayEnvironment()

  const onSave = useCallback(
    (newSelectedProjects: ProjectPickerRelayProject[]) => {
      const projectsToAdd = newSelectedProjects.filter(a => !selectedProjects.some(aa => aa.id === a.id))
      const projectsToRemove = selectedProjects.filter(a => !newSelectedProjects.some(aa => aa.id === a.id))
      if (!projectsToAdd.length && !projectsToRemove.length) {
        return
      }
      commitAddIssuesToProjectPickerBulkMutation({
        environment,
        input: {
          ids: [...issueIds],
          addToProjectV2Ids: projectsToAdd.map(l => l.id),
          removeFromProjectV2Ids: projectsToRemove.map(l => (l.id ? l.id.toString() : '')),
        },
        onCompleted: response => {
          onCompleted?.(response.updateIssuesBulk?.jobId || undefined)
        },
        onError: (error: Error) => {
          onError?.(error)
        },
      })
    },
    [environment, issueIds, onCompleted, onError, selectedProjects],
  )

  return ProjectPicker({
    pickerId, // Should be issue specific, if loaded for a specific issue :)
    readonly,
    selectedProjects: [...selectedProjects], // Clone so we keep the initial state
    insidePortal,
    onSave,
    anchorElement,
    nested,
    owner,
    repo,
    triggerOpen,
  })
}

try{ BulkProjectPickerRelay.displayName ||= 'BulkProjectPickerRelay' } catch {}